import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {usePlanner} from '../context/PlannerContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft, FiArrowRight, FiUser, FiPlus, FiX} = FiIcons;

function ThriveSurvey() {
  const navigate = useNavigate();
  const {dispatch} = usePlanner();
  
  // Always start with fresh data to avoid issues
  const [students, setStudents] = useState([createEmptyStudent()]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  function createEmptyStudent() {
    return {
      name: '',
      // Questions 1-25 with Yes/No responses
      q1: '', q2: '', q3: '', q4: '', q5: '', q6: '', q7: '', q8: '', q9: '', q10: '',
      q11: '', q12: '', q13: '', q14: '', q15: '', q16: '', q17: '', q18: '', q19: '', q20: '',
      q21: '', q22: '', q23: '', q24: '', q25: ''
    };
  }

  const questions = [
    'Can the student blend individual sounds?',
    'Can the student segment sounds?',
    'Can the student isolate beginning/middle/end sounds?',
    'Can the student manipulate sounds?',
    'Knows letter-sound correspondences?',
    'Can decode CVC words?',
    'Can read short vowel words?',
    'Can read common digraphs?',
    'Can decode blends?',
    'Can decode multisyllabic words?',
    'Reads without sounding out?',
    'Reads with phrasing and expression?',
    'Rereads for fluency?',
    'Recognizes high-frequency words?',
    'Can retell story in order?',
    'Can answer questions about text?',
    'Can identify main idea/details?',
    'Can make predictions/inferences?',
    'Reads words without relying on pictures/context?',
    'Avoids guessing words based on visuals?',
    'Writes names/words with sound knowledge?',
    'Spells basic words correctly, not missing sounds?',
    'Uses syllables or morphemes to decode?',
    'Spells with morphemes or syllables?',
    'Writes with spelling automaticity?'
  ];

  const handleInputChange = (field, value) => {
    const updatedStudents = [...students];
    updatedStudents[currentStudentIndex] = {...updatedStudents[currentStudentIndex], [field]: value};
    setStudents(updatedStudents);
  };

  const addStudent = () => {
    setStudents([...students, createEmptyStudent()]);
    setCurrentStudentIndex(students.length);
  };

  const deleteStudent = (indexToDelete) => {
    if (students.length === 1) return;
    const updatedStudents = students.filter((_, index) => index !== indexToDelete);
    setStudents(updatedStudents);
    if (indexToDelete === currentStudentIndex) {
      setCurrentStudentIndex(indexToDelete > 0 ? indexToDelete - 1 : 0);
    } else if (indexToDelete < currentStudentIndex) {
      setCurrentStudentIndex(currentStudentIndex - 1);
    }
  };

  const selectStudent = (index) => {
    setCurrentStudentIndex(index);
  };

  const processFormData = async () => {
    console.log('🔄 Starting Thrive Survey processing...'); 
    setIsProcessing(true);
    
    try {
      // Add a small delay to show the processing state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const processedStudents = students.map((student, index) => {
        console.log(`📋 Processing student ${index + 1}:`, student.name);
        
        if (!student.name.trim()) {
          console.log(`⚠️ Skipping student ${index + 1} - no name`);
          return null;
        }

        // Convert Yes/No responses to numeric values for calculation
        const responses = [];
        let answeredQuestions = 0;
        
        for (let i = 1; i <= 25; i++) {
          const response = student[`q${i}`];
          const numericValue = response === 'Yes' ? 1 : (response === 'No' ? 0 : 0);
          responses.push(numericValue);
          if (response !== '') answeredQuestions++;
        }

        console.log(`📊 Student ${student.name} answered ${answeredQuestions}/25 questions`);
        console.log(`📈 Responses:`, responses);

        // Calculate skill percentages based on question groupings
        // Questions 1-4: Phonological Awareness
        const phonologicalScore = responses.slice(0, 4).reduce((a, b) => a + b, 0) / 4 * 100;

        // Question 5: Letter-Sound Knowledge
        const letterSoundsScore = responses[4] * 100;

        // Questions 6-7: Basic CVC decoding
        const cvcScore = responses.slice(5, 7).reduce((a, b) => a + b, 0) / 2 * 100;

        // Questions 8-10: Advanced decoding
        const advancedDecoding = responses.slice(7, 10);
        const digraphScore = responses[7] * 100;
        const blendScore = responses[8] * 100;
        const multisyllabicBase = responses[9] * 100;

        // Estimate letter names based on letter sounds performance
        let letterNamesScore = 90;
        if (letterSoundsScore < 100) {
          letterNamesScore = Math.max(70, letterSoundsScore - 10);
        }

        const processedStudent = {
          name: student.name,
          letterNames: Math.round(letterNamesScore),
          letterSounds: Math.round(letterSoundsScore),
          vcCvc: Math.round(cvcScore),
          cvccCcvc: Math.round(blendScore * 0.9),
          cvce: Math.round(advancedDecoding.reduce((a, b) => a + b, 0) / 3 * 85),
          rControlled: Math.round(advancedDecoding.reduce((a, b) => a + b, 0) / 3 * 75),
          consonantDigraphs: Math.round(digraphScore),
          vowelDigraphs: Math.round(advancedDecoding.reduce((a, b) => a + b, 0) / 3 * 70),
          prefixesSuffixes: Math.round(responses.slice(22, 24).reduce((a, b) => a + b, 0) / 2 * 100),
          twoSyllable: Math.round(multisyllabicBase * 0.9),
          threeSyllable: Math.round(multisyllabicBase * 0.8),
          fourSyllable: Math.round(multisyllabicBase * 0.7)
        };

        console.log(`✅ Processed student ${student.name}:`, processedStudent);
        return processedStudent;
      }).filter(student => student !== null);

      console.log(`🎯 Final processed students (${processedStudents.length}):`, processedStudents);

      if (processedStudents.length === 0) {
        console.error('❌ No valid students to process');
        alert('Please ensure at least one student has a name and some questions answered.');
        setIsProcessing(false);
        return;
      }

      // Update the context with processed data
      console.log('💾 Updating context with assessment type: THRIVE');
      dispatch({type: 'SET_ASSESSMENT_TYPE', payload: 'THRIVE'});
      
      console.log('💾 Updating context with students:', processedStudents);
      dispatch({type: 'SET_STUDENTS', payload: processedStudents});
      
      console.log('🚀 Navigating to groups page...');
      
      // Add a small delay before navigation to ensure state is updated
      setTimeout(() => {
        navigate('/groups');
        setIsProcessing(false);
      }, 200);
      
    } catch (error) {
      console.error('❌ Error processing Thrive survey data:', error);
      alert(`There was an error processing the survey data: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const isCurrentStudentValid = () => {
    const currentStudent = students[currentStudentIndex];
    if (!currentStudent.name.trim()) return false;
    
    // Check if at least one question is answered
    for (let i = 1; i <= 25; i++) {
      if (currentStudent[`q${i}`] !== '') {
        return true;
      }
    }
    return false;
  };

  const currentStudent = students[currentStudentIndex];

  // Show processing state
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-thrive-mint border-t-thrive-teal rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-thrive-slate mb-2 font-display">Processing Thrive Survey...</h2>
          <p className="text-gray-600">Converting responses to skill data</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <motion.header
        initial={{opacity: 0, y: -20}}
        animate={{opacity: 1, y: 0}}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/assessment')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Assessment Input
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">Thrive Literacy Corner Small Group Assessment</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-thrive-slate text-white px-6 py-4">
            <h2 className="text-xl font-bold text-center tracking-wider">Student Reading Skills Checklist</h2>
            <p className="text-center text-sm opacity-90 mt-1">Use this sheet to record Yes/No for each skill, along with space for notes.</p>
          </div>

          <div className="p-6">
            {/* Student Tabs */}
            {students.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
                {students.map((student, index) => (
                  <div key={index} className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentStudentIndex === index 
                      ? 'bg-thrive-teal text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                    <button onClick={() => selectStudent(index)} className="pr-6">
                      {student.name || `Student ${index + 1}`}
                    </button>
                    {students.length > 1 && (
                      <button
                        onClick={(e) => {e.stopPropagation(); deleteStudent(index);}}
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Delete student"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Student Info */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <SafeIcon icon={FiUser} className="inline mr-1" />
                  Student Name
                </label>
                <input
                  type="text"
                  value={currentStudent.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                  placeholder="Enter student name"
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-thrive-slate mb-6 tracking-wider">READING SKILLS ASSESSMENT</h3>

            {/* Skills Checklist */}
            <div className="space-y-3 mb-8">
              {questions.map((question, index) => (
                <div key={index} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg hover:border-thrive-mint transition-all">
                  <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-sm font-medium text-thrive-slate">
                    {index + 1}
                  </div>
                  <div className="flex-1 text-sm text-gray-700">
                    {question}
                  </div>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name={`q${index + 1}_${currentStudentIndex}`}
                        value="Yes"
                        checked={currentStudent[`q${index + 1}`] === 'Yes'}
                        onChange={(e) => handleInputChange(`q${index + 1}`, e.target.value)}
                        className="text-thrive-teal focus:ring-thrive-teal"
                      />
                      <span className="text-sm font-medium text-green-600">Yes</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name={`q${index + 1}_${currentStudentIndex}`}
                        value="No"
                        checked={currentStudent[`q${index + 1}`] === 'No'}
                        onChange={(e) => handleInputChange(`q${index + 1}`, e.target.value)}
                        className="text-red-500 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-red-600">No</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={addStudent}
                className="bg-thrive-mint hover:bg-thrive-teal text-thrive-slate hover:text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md"
              >
                <SafeIcon icon={FiPlus} />
                Add Student
              </button>
              <button
                onClick={processFormData}
                disabled={!isCurrentStudentValid() || isProcessing}
                className="bg-thrive-teal hover:bg-thrive-slate disabled:bg-gray-300 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg disabled:shadow-none"
              >
                Continue to Groups
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default ThriveSurvey;